import { Component, ElementRef, EventEmitter, HostListener, Input, Output } from '@angular/core';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-date-range',
  templateUrl: './date-range.component.html',
  styleUrls: ['./date-range.component.scss']
})
export class DateRangeComponent {
  @Input() placeholder = 'Chọn ngày';
  @Input() set from(value: string | null) { this.fromDate = this.parse(value); }
  @Input() set to(value: string | null) { this.toDate = this.parse(value); }
  @Output() fromChange = new EventEmitter<string>();
  @Output() toChange = new EventEmitter<string>();

  public fromDate: NgbDate | null = null;
  public toDate: NgbDate | null = null;
  public hoveredDate: NgbDate | null = null;
  public isOpen = false;
  private picking = false;

  constructor(private elementRef: ElementRef) { }

  private today = (() => {
    const now = new Date();
    return new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  })();

  get startDate(): NgbDate {
    return this.fromDate ?? this.today;
  }

  get label(): string {
    if (!this.fromDate) {
      return this.placeholder;
    }
    if (!this.toDate || this.toDate.equals(this.fromDate)) {
      return this.format(this.fromDate);
    }
    return this.format(this.fromDate) + ' - ' + this.format(this.toDate);
  }

  onDateSelection(date: NgbDate) {
    if (!this.picking) {
      this.picking = true;
      this.fromDate = date;
      this.toDate = date;
      this.emit();
      return;
    }
    if (date.before(this.fromDate)) {
      this.toDate = this.fromDate;
      this.fromDate = date;
    } else {
      this.toDate = date;
    }
    this.picking = false;
    this.emit();
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.picking = false;
  }

  clear(event?: Event) {
    event?.stopPropagation();
    this.picking = false;
    this.fromDate = null;
    this.toDate = null;
    this.hoveredDate = null;
    this.emit();
  }

  isHovered(date: NgbDate) {
    return this.picking && !!this.fromDate && !!this.hoveredDate
      && date.after(this.fromDate) && date.before(this.hoveredDate);
  }

  isInside(date: NgbDate) {
    return !!this.toDate && date.after(this.fromDate) && date.before(this.toDate);
  }

  isRange(date: NgbDate) {
    return date.equals(this.fromDate) || (!!this.toDate && date.equals(this.toDate))
      || this.isInside(date) || this.isHovered(date);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      this.picking = false;
    }
  }

  private emit() {
    this.fromChange.emit(this.fromDate ? this.toIso(this.fromDate) : '');
    this.toChange.emit(this.toDate ? this.toIso(this.toDate) : '');
  }

  private parse(value: string | null): NgbDate | null {
    if (!value) {
      return null;
    }
    const [y, m, d] = value.split('-').map(Number);
    return y && m && d ? new NgbDate(y, m, d) : null;
  }

  private toIso(d: NgbDate) {
    return `${d.year}-${String(d.month).padStart(2, '0')}-${String(d.day).padStart(2, '0')}`;
  }

  private format(d: NgbDate) {
    return `${String(d.day).padStart(2, '0')}/${String(d.month).padStart(2, '0')}/${d.year}`;
  }
}
